import Handyman from "@mui/icons-material/Handyman";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";

import type { FlagRow } from "./FlagsTab";

import EncampLogo from "../../../assets/EncampLogo.svg";
import { formatPercent } from "./flagAdoption";

export const TYPE_LABEL: Record<FlagRow["type"], string> = {
  MODULE: "Module",
  ADD_ON: "Add-on",
  OTHER: "Other",
};

export const TYPE_FILTER_VALUES = Object.keys(TYPE_LABEL) as FlagRow["type"][];

/**
 * Adoption stat cell — count + parenthesized percent in muted text.
 * Rendered as a two-column grid so counts line up on one right edge and
 * percentages line up on another, regardless of their digit lengths
 * (e.g. `5  (10%)` and `120  (75%)` produce visually aligned columns).
 * The percent is in `text.secondary` to deemphasize it relative to the
 * raw count; the tooltip spells out absolute numbers.
 */
function AdoptionCell({
  numerator,
  denominator,
  offCount,
}: {
  numerator: number;
  denominator: number;
  offCount: number;
}) {
  // One line per stat so the tooltip reads as a quick legend rather
  // than a run-on sentence. We always show enabled + disabled (even
  // when zero) so the order is predictable; the "total" row only
  // appears when we actually have a denominator to anchor it to.
  const tooltipContent = (
    <Stack spacing={0} sx={{ fontVariantNumeric: "tabular-nums" }}>
      <Box>{numerator} explicitly enabled</Box>
      <Box>{offCount} explicitly disabled</Box>
      {denominator > 0 && <Box>{denominator} active customer tenants</Box>}
    </Stack>
  );
  return (
    <Tooltip title={tooltipContent} placement="top">
      <Box
        sx={{
          width: "100%",
          display: "grid",
          // `auto auto` keeps each sub-column tight to its content while
          // still right-aligning each value within its slot — counts
          // line up on one right edge, percentages on another.
          gridTemplateColumns: "auto auto",
          columnGap: 0.5,
          justifyContent: "end",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <Box sx={{ textAlign: "right" }}>{numerator}</Box>
        <Box sx={{ textAlign: "right", color: "text.secondary", minWidth: 44 }}>
          {denominator > 0 ? `(${formatPercent(numerator, denominator)})` : ""}
        </Box>
      </Box>
    </Tooltip>
  );
}

function FlagLabelCell({ row }: { row: FlagRow }) {
  // The label is a short identifier; the FF's description carries the
  // explanatory copy. Surface it on hover so staff can scan the grid
  // without opening the detail drawer for every row.
  const label = (
    <Typography variant="body2" sx={{ py: 0.5 }} noWrap>
      {row.label}
    </Typography>
  );
  if (!row.description) return label;
  return (
    <Tooltip title={row.description} placement="top">
      {label}
    </Tooltip>
  );
}

function ParentModuleCell({ row }: { row: FlagRow }) {
  if (!row.parentLabel) return null;
  return (
    <Typography variant="body2" noWrap>
      {row.parentLabel}
    </Typography>
  );
}

function StaffOnlyCell({ row }: { row: FlagRow }) {
  if (!row.staffOverride) return null;
  return (
    <Tooltip title="Staff users see this feature regardless of the flag value">
      <Box
        component="img"
        src={EncampLogo}
        alt="Staff-visible"
        aria-label="Staff-visible"
        sx={{ width: 18, height: 18, display: "block" }}
      />
    </Tooltip>
  );
}

function InDevelopmentCell({ row }: { row: FlagRow }) {
  if (!row.inDevelopment) return null;
  return (
    <Tooltip title="This feature is currently being developed">
      <Handyman
        fontSize="small"
        // Muted (text.secondary) so the icon reads as neutral metadata
        // rather than a warning — "in development" is informational,
        // not a problem.
        sx={{ color: "text.secondary", display: "block" }}
        aria-label="In Development"
      />
    </Tooltip>
  );
}

function DiscrepancyCell({ row }: { row: FlagRow }) {
  // Non-mappable flags render a muted em-dash rather than a fully
  // empty cell — without it the column reads as 18 "blank?" rows and
  // only 4 with data, which staff have flagged as confusing. The dash
  // makes "this row was intentionally not measured" obvious at a
  // glance and the tooltip explains why for the curious.
  if (row.subscriptionDiscrepancyCount === null || !row.subscriptionDiscrepancyBreakdown) {
    return (
      <Tooltip
        title="Not applicable — this flag isn't tied to a subscription module"
        placement="top"
      >
        <Typography
          variant="body2"
          aria-label="Not applicable"
          sx={{ color: "text.disabled", fontVariantNumeric: "tabular-nums" }}
        >
          —
        </Typography>
      </Tooltip>
    );
  }
  const { flagOnSubOffCount, flagOffSubOnCount } = row.subscriptionDiscrepancyBreakdown;
  const total = row.subscriptionDiscrepancyCount;
  const tooltipContent = (
    <Stack spacing={0} sx={{ fontVariantNumeric: "tabular-nums" }}>
      <Box>{flagOnSubOffCount} flag on, subscription off</Box>
      <Box>{flagOffSubOnCount} subscription on, flag off</Box>
    </Stack>
  );
  return (
    <Tooltip title={tooltipContent} placement="top">
      <Chip
        label={total}
        size="small"
        color={total > 0 ? "warning" : "default"}
        aria-label={`${total} subscription discrepancies`}
      />
    </Tooltip>
  );
}

/**
 * Shared renderer for the two annotation timestamp columns (Created at /
 * Updated at). The cell shows the date alone — the grid is scanned rather
 * than read closely, and a full timestamp per row is too much ink — while
 * the tooltip carries the precise time and the acting user. `verb` seeds
 * that tooltip ("Created …" / "Last edited …") so the wording matches the
 * detail drawer's annotation footer.
 */
function AnnotationTimestampCell({
  isoTimestamp,
  verb,
  actor,
}: {
  isoTimestamp: string | null;
  verb: string;
  actor: string | null;
}) {
  if (!isoTimestamp) return null;
  const at = new Date(isoTimestamp);
  return (
    <Tooltip title={`${verb} ${at.toLocaleString()}${actor ? ` by ${actor}` : ""}`} placement="top">
      <Typography variant="body2" noWrap sx={{ fontVariantNumeric: "tabular-nums" }}>
        {at.toLocaleDateString()}
      </Typography>
    </Tooltip>
  );
}

function TagsCell({ row }: { row: FlagRow }) {
  if (row.tags.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
      {row.tags.map((tag) => (
        <Chip key={tag} label={tag} size="small" />
      ))}
    </Stack>
  );
}

function DefaultValueCell({ row }: { row: FlagRow }) {
  return (
    <Tooltip title={`This flag defaults to ${row.defaultValue ? "ON" : "OFF"}`}>
      {/* Very pale green fill (`success.light` lightened ~70%)
          with `success.dark` text — calmer than the standard
          `success.light` shade so the chip reads as quiet
          metadata in a grid cell rather than a status alert.
          OFF keeps the neutral default chip. */}
      <Chip
        label={row.defaultValue ? "ON" : "OFF"}
        size="small"
        sx={
          row.defaultValue
            ? (theme) => ({
                bgcolor: lighten(theme.palette.success.light, 0.7),
                color: theme.palette.success.dark,
              })
            : undefined
        }
      />
    </Tooltip>
  );
}

/**
 * The two annotation timestamp columns, kept together so they stay
 * identically sized and formatted — staff read them as a pair ("written
 * down when" vs. "last changed when"). Neither depends on the adoption
 * denominator, so they live at module level rather than being rebuilt
 * per call.
 */
const ANNOTATION_TIMESTAMP_COLUMNS: OmnisearchGridColDef<FlagRow>[] = [
  {
    field: "annotationCreatedAt",
    headerName: "Created at",
    flex: 0.5,
    minWidth: 110,
    valueGetter: (_value, row) => row.annotationCreatedAt ?? "",
    renderCell: ({ row }) => (
      <AnnotationTimestampCell
        isoTimestamp={row.annotationCreatedAt}
        verb="Created"
        actor={row.annotationCreatedBy}
      />
    ),
  },
  {
    field: "annotationUpdatedAt",
    headerName: "Updated at",
    flex: 0.5,
    minWidth: 110,
    valueGetter: (_value, row) => row.annotationUpdatedAt ?? "",
    renderCell: ({ row }) => (
      <AnnotationTimestampCell
        isoTimestamp={row.annotationUpdatedAt}
        verb="Last edited"
        actor={row.annotationUpdatedBy}
      />
    ),
  },
];

/**
 * Column set for the flag-centric grid. Order: Flag → Parent module →
 * Type → Staff-visible → Default → In Development → Adoption →
 * Discrepancies → Created at → Updated at → Tags. Parent module sits
 * second so related flags read together when sorted by the (default)
 * Flag column. The two annotation timestamps sit side by side so staff
 * can compare "when was this written down" against "when did it last
 * change" without crossing the grid. Parent + Adoption are tightened up
 * since their content is short; In Development gets a touch more room
 * for its full header. Tags gets the leftover space so multi-chip rows
 * wrap cleanly.
 *
 * A plain function (rather than a hook) so `FlagsTab` can memoize it on
 * its single input, the adoption denominator.
 */
export function buildFlagColumns(customerTenantCount: number): OmnisearchGridColDef<FlagRow>[] {
  return [
    {
      field: "label",
      headerName: "Flag",
      flex: 1.2,
      minWidth: 200,
      renderCell: ({ row }) => <FlagLabelCell row={row} />,
    },
    {
      field: "parentLabel",
      headerName: "Parent module",
      flex: 0.5,
      minWidth: 110,
      valueGetter: (_value, row) => row.parentLabel ?? "",
      renderCell: ({ row }) => <ParentModuleCell row={row} />,
    },
    {
      field: "type",
      headerName: "Type",
      flex: 0.5,
      minWidth: 110,
      valueGetter: (_value, row) => TYPE_LABEL[row.type],
    },
    {
      field: "staffOverride",
      headerName: "Staff-visible",
      flex: 0.4,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => <StaffOnlyCell row={row} />,
    },
    {
      field: "defaultValue",
      headerName: "Default ON/OFF",
      flex: 0.4,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => (row.defaultValue ? "ON" : "OFF"),
      renderCell: ({ row }) => <DefaultValueCell row={row} />,
    },
    {
      field: "inDevelopment",
      headerName: "In Development",
      flex: 0.5,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => <InDevelopmentCell row={row} />,
    },
    {
      field: "tenantOnCount",
      headerName: "Adoption",
      flex: 0.5,
      minWidth: 110,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => (
        <AdoptionCell
          numerator={row.tenantOnCount}
          denominator={customerTenantCount}
          offCount={row.tenantOffCount}
        />
      ),
    },
    {
      field: "subscriptionDiscrepancyCount",
      headerName: "Discrepancies",
      flex: 0.5,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      // Sortable via custom comparator in sortFlagRows; null rows
      // sort to the bottom regardless of asc/desc.
      valueGetter: (_value, row) =>
        row.subscriptionDiscrepancyCount === null ? "" : row.subscriptionDiscrepancyCount,
      renderCell: ({ row }) => <DiscrepancyCell row={row} />,
    },
    ...ANNOTATION_TIMESTAMP_COLUMNS,
    {
      field: "tags",
      headerName: "Tags",
      flex: 1.1,
      minWidth: 180,
      sortable: false,
      valueGetter: (_value, row) => row.tags.join(", "),
      renderCell: ({ row }) => <TagsCell row={row} />,
    },
  ];
}
